import { TextInput } from '@any-ui-react/core';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DatePicker } from '@/common/components/shared/date-picker/DatePicker';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import type { EmployeePersonalInformationFormDataType } from '@/common/types';
import { DateUtils } from '@/common/utils';

export const PassportFormFields = () => {
  const {
    formState: { errors },
    register,
    setValue,
    control,
  } = useFormContext<EmployeePersonalInformationFormDataType>();

  const passportIssuedDate = useWatch({ control, name: 'passportIssuedDate' });
  const passportExpiryDate = useWatch({ control, name: 'passportExpiryDate' });

  const { employeeDetail } = useEmployeeDetailContext();
  const isNewRecord = !employeeDetail.id;
  const { hasEmployeePersonalInformationEditAccess } = useEmployeeAccess();
  const isEditable = hasEmployeePersonalInformationEditAccess || isNewRecord;

  const { text: passportIssuedDateText, isFuture: passportIssuedDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(passportIssuedDate),
    [passportIssuedDate],
  );
  const { text: passportExpiryDateText, isFuture: passportExpiryDateIsFuture } = useMemo(
    () => DateUtils.getDifferenceString(passportExpiryDate),
    [passportExpiryDate],
  );

  return (
    <div className="mb-6">
      <div className="mb-4 font-semibold">Passport</div>
      <fieldset className="form-fieldset">
        <TextInput
          {...register('passportNumber')}
          autoComplete="off"
          className="col-span-2 lg:col-span-1"
          data-1p-ignore={true}
          disabled={!isEditable}
          label="Passport Number"
          placeholder="Enter Passport Number"
        />

        <TextInput
          {...register('passportName')}
          autoComplete="off"
          className="col-span-2 lg:col-span-1"
          data-1p-ignore={true}
          disabled={!isEditable}
          error={errors.passportName?.message}
          label="Full Name on Passport"
          placeholder="Enter Full Name on Passport"
        />

        <TextInput
          {...register('passportIssuedPlace')}
          autoComplete="off"
          className="col-span-2 lg:col-span-1"
          data-1p-ignore={true}
          disabled={!isEditable}
          error={errors.passportIssuedPlace?.message}
          label="Passport Issued Place"
          placeholder="Enter Passport Issued Place"
        />

        <div className="col-span-2 lg:col-span-1">
          <DatePicker
            className="col-span-2 lg:col-span-1"
            clearable={true}
            disabled={!isEditable}
            error={errors.passportIssuedDate?.message}
            label="Passport Issued Date"
            placeholder="yyyy-MM-dd"
            value={passportIssuedDate || undefined}
            onChange={value => {
              setValue('passportIssuedDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {passportIssuedDateText}
            {!passportIssuedDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1">
          <DatePicker
            clearable={true}
            disabled={!isEditable}
            error={errors.passportExpiryDate?.message}
            label="Passport Expiry Date"
            placeholder="yyyy-MM-dd"
            value={passportExpiryDate || undefined}
            onChange={value => {
              setValue('passportExpiryDate', DateUtils.formatDate(value), {
                shouldDirty: true,
              });
            }}
          />
          <div className="text-gray-7 text-xs ml-3">
            {passportExpiryDateText}
            {!passportExpiryDateIsFuture ? ' ago' : ''}
          </div>
        </div>

        <div>
          <TextInput
            {...register('placeOfBirth')}
            autoComplete="off"
            className="col-span-2 lg:col-span-1"
            data-1p-ignore={true}
            disabled={!isEditable}
            error={errors.placeOfBirth?.message}
            label="Place of Birth"
            placeholder="Enter Place of Birth"
          />
        </div>
      </fieldset>
    </div>
  );
};
